import React, { useState, useRef } from "react";
import {
  Grid,
  TextField,
  DataGrid,
  Autocomplete,
  CircularProgress,
  Button,
} from "../../../../../components";
import {
  responseErros,
  formatPlacaVeiculo,
  formatCpf,
} from "../../../../../utils";
import {
  PessoaService,
  notification,
  MdfeService,
  MdfeCondutoresService,
  VeiculosService,
  MdfeReboquesService,
} from "../../../../../services";
import shortid from "shortid";

const pessoaService = new PessoaService();
const mdfeService = new MdfeService();
const mdfeCondutoresService = new MdfeCondutoresService();
const veiculosService = new VeiculosService();
const mdfeReboquesService = new MdfeReboquesService();

export default function TabConjuntoTransporte({
  setCondutoresMdfe,
  condutoresMdfe,
  id,
  setLoading,
  status,
  reboques,
  setReboques,
  montarReboques,
}) {
  const [loadingAutoCompleteReboque, setLoadingAutoCompleteReboque] =
    useState(false);
  const [loadingAutoCompleteCondutor, setLoadingAutoCompleteCondutor] =
    useState(false);
  const [veiculos, setVeiculos] = useState([]);
  const [reboque, setReboque] = useState(null);
  const [erroReboque, setErroReboque] = useState(false);
  const [condutores, setCondutores] = useState([]);
  const [erroCondutor, setErroCondutor] = useState(false);
  const [condutor, setCondutor] = useState(null);
  const inputCondutor = useRef();
  const inputReboque = useRef();
  const condutoresColunas = [
    {
      field: "cpf",
      headerName: "CPF",
      flex: 200,
      valueGetter: (params) => formatCpf(params.value),
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 200,
    },
  ];
  const reboqueColunas = [
    {
      field: "placa",
      headerName: "Placa",
      valueGetter: (params) => formatPlacaVeiculo(params.value),
      flex: 200,
    },
    {
      field: "renavam",
      headerName: "RENAVAM",
      flex: 150,
    },
  ];

  async function buscarCondutores(filter) {
    const result = await pessoaService.getAll(filter);
    if (!result.isAxiosError) {
      setCondutores(result.data);
    } else {
      responseErros(result);
    }
  }

  const onSearchChangeCondutor = async (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const filtros = {
        nomeRazaoSocial: event.target?.value,
        motorista: true,
        nonPaginated: true,
      };
      setLoadingAutoCompleteCondutor(true);
      await buscarCondutores(filtros);
      setLoadingAutoCompleteCondutor(false);
    }
  };

  const onChangeAutocompleteCondutor = (value) => {
    setCondutor(value ?? undefined);
  };

  const onClickAdicionarCondutor = () => {
    if (!condutor) {
      setErroCondutor(true);
      return notification.erroValidacao("Condutor");
    }
    const condutorAdicao = {
      id: shortid.generate(),
      pessoaId: condutor.id,
      cpf: condutor.cnpjCpf,
      nome: condutor.nomeRazaoSocial,
    };
    if (id) {
      cadastrarCondutor(condutorAdicao);
    } else {
      setCondutoresMdfe([...condutoresMdfe, condutorAdicao]);
    }
    setErroCondutor(false);
    setCondutores([]);
    setCondutor(null);
  };

  async function cadastrarCondutor(condutorAdicao) {
    setLoading(true);
    const response = await mdfeService.cadastrarCondutor(id, condutorAdicao);
    if (!response.isAxiosError) {
      await buscarCondutoresMdfe();
      setLoading(false);
      notification.cadastroSucesso();
    } else {
      setLoading(false);
      responseErros(response);
    }
  }

  async function buscarCondutoresMdfe() {
    const filter = {
      fatMdfeId: id,
      nonPaginated: true,
    };
    const response = await mdfeCondutoresService.getAll(filter);
    if (!response.isAxiosError) {
      setCondutoresMdfe(response.data);
    } else {
      responseErros(response);
    }
  }

  async function deletarCondutor(idCondutor) {
    setLoading(true);
    const response = await mdfeCondutoresService.deletar(idCondutor);
    if (!response.isAxiosError) {
      await buscarCondutoresMdfe();
      setLoading(false);
      notification.deletadoSucesso();
    } else {
      setLoading(false);
      responseErros(response);
    }
  }

  const onclickDeletarCondutor = (idCondutor) => {
    if (id) {
      deletarCondutor(idCondutor);
    } else {
      setCondutoresMdfe(
        condutoresMdfe.filter((condutorMdfe) => condutorMdfe.id != idCondutor)
      );
    }
  };

  async function buscarVeiculos(filter) {
    const result = await veiculosService.getAll(filter);
    if (!result.isAxiosError) {
      setVeiculos(result.data);
    } else {
      responseErros(result);
    }
  }

  const onSearchChangeReboque = async (event, inputRef) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const filtros = {
        placa: event.target?.value.replaceAll("-", ""),
        renavan: event.target?.value,
        nonPaginated: true,
        tipo: "REBOQUE",
        restritiva: true,
      };
      setLoadingAutoCompleteReboque(true);
      await buscarVeiculos(filtros);
      setLoadingAutoCompleteReboque(false);
    }
  };

  const onChangeAutocompleteReboque = (value) => {
    setReboque(value);
  };

  const onClickAdicionarReboque = () => {
    if (!reboque) {
      setErroReboque(true);
      return notification.erroValidacao("Reboque");
    }
    const reboqueAdicao = {
      id: shortid.generate(),
      veiculo: reboque,
      fatVeiculoId: reboque.id,
      placa: reboque.placa,
      renavam: reboque.renavam,
    };
    if (id) {
      cadastrarReboque(reboqueAdicao);
    } else {
      setReboques([...reboques, reboqueAdicao]);
    }
    setReboque(null);
    setVeiculos([]);
    setErroReboque(false);
  };

  async function cadastrarReboque(reboqueAdicao) {
    setLoading(true);
    const response = await mdfeService.cadastrarReboque(id, reboqueAdicao);
    if (!response.isAxiosError) {
      await buscarReboques();
      setLoading(false);
      notification.cadastroSucesso();
    } else {
      setLoading(false);
      responseErros(response);
    }
  }

  async function buscarReboques() {
    const filter = {
      fatMdfeId: id,
      nonPaginated: true,
    };
    const response = await mdfeReboquesService.getAll(filter);
    if (!response.isAxiosError) {
      montarReboques(response.data);
    } else {
      responseErros(response);
    }
  }

  async function deletarReboque(idReboque) {
    setLoading(true);
    const response = await mdfeReboquesService.deletar(idReboque);
    if (!response.isAxiosError) {
      await buscarReboques();
      setLoading(false);
      notification.deletadoSucesso();
    } else {
      setLoading(false);
      responseErros(response);
    }
  }

  const onclickDeletarReboque = (idReboque) => {
    if (id) {
      deletarReboque(idReboque);
    } else {
      setReboques(
        reboques.filter((reboqueMdfe) => reboqueMdfe.id != idReboque)
      );
    }
  };

  return (
    <Grid container>
      <Grid container xs={6} spacing={2} className="mt-2">
        <Grid item xs={10}>
          <Autocomplete
            id="condutor"
            name="condutor"
            options={condutores}
            autoHighlight
            getOptionLabel={(option) => option?.nomeRazaoSocial}
            loading={loadingAutoCompleteReboque}
            loadingText="Carregando"
            noOptionsText={
              document.getElementById("condutor")?.value.length >= 1 ? (
                <Grid container spacing={1}>
                  <Grid item xs={12} fullWidth>
                    Nenhum condutor encontrado
                  </Grid>
                </Grid>
              ) : (
                "Digite e pressione Enter"
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Condutor"
                error={erroCondutor}
                variant={"outlined"}
                onKeyDown={(e) => onSearchChangeCondutor(e, inputCondutor)}
                inputRef={inputCondutor}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingAutoCompleteCondutor ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={(_, newValue) => onChangeAutocompleteCondutor(newValue)}
            value={condutor ?? ""}
          />
        </Grid>
        <Grid item xs={2} className="mt-2">
          <Button
            id="buttonAdicionar"
            variant="contained"
            color="success"
            size="small"
            disabled={status != "PENDENTE" ? true : false}
            sx={{
              height: "35px",
              borderRadius: "50%",
              minWidth: "15px",
              boxShadow: "none",
            }}
            onClick={onClickAdicionarCondutor}
          >
            <i
              className="ph-bold ph-plus"
              style={{ color: "#fff", fontSize: 15 }}
            ></i>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            style={{ zIndex: 1 }}
            rows={condutoresMdfe}
            columns={condutoresColunas}
            rowCount={condutoresMdfe.length}
            onClickDelete={onclickDeletarCondutor}
            deleteShow={status != "PENDENTE" ? false : true}
          />
        </Grid>
      </Grid>
      <Grid
        container
        xs={1}
        fullWidth
        className="d-flex justify-content-center"
      >
        <hr className="vr" />
      </Grid>
      <Grid container xs={5} spacing={2} className="mt-2">
        <Grid item xs={10}>
          <Autocomplete
            id="reboque"
            name="reboque"
            options={veiculos}
            autoHighlight
            getOptionLabel={(option) => formatPlacaVeiculo(option.placa)}
            filterSelectedOptions
            filterOptions={(options, state) => {
              const input = state.inputValue.replaceAll("-", "").toUpperCase();
              return options.filter((item) =>
                item.placa.toUpperCase().includes(input)
              );
            }}
            loading={loadingAutoCompleteReboque}
            loadingText="Carregando"
            noOptionsText={
              document.getElementById("reboque")?.value.length >= 1 ? (
                <Grid container spacing={1}>
                  <Grid item xs={12} fullWidth>
                    Nenhum reboque encontrado
                  </Grid>
                </Grid>
              ) : (
                "Digite e pressione Enter"
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reboque"
                onKeyDown={(e) => onSearchChangeReboque(e, inputReboque)}
                inputRef={inputReboque}
                error={erroReboque}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingAutoCompleteReboque ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={(_, newValue) => onChangeAutocompleteReboque(newValue)}
            value={reboque}
          />
        </Grid>
        <Grid item xs={2} className="mt-2">
          <Button
            id="buttonAdicionar"
            variant="contained"
            color="success"
            size="small"
            disabled={status != "PENDENTE" ? true : false}
            sx={{
              height: "35px",
              borderRadius: "50%",
              minWidth: "15px",
              boxShadow: "none",
            }}
            onClick={onClickAdicionarReboque}
          >
            <i
              className="ph-bold ph-plus"
              style={{ color: "#fff", fontSize: 15 }}
            ></i>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            style={{ zIndex: 1 }}
            rows={reboques}
            columns={reboqueColunas}
            rowCount={reboques.length}
            onClickDelete={onclickDeletarReboque}
            deleteShow={status != "PENDENTE" ? false : true}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}
